import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import { Button, Fade, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import {
  Estate,
  EstateImport,
  EstateListDataGridColumns,
  FeatureSubscriptionType,
  PortalPermissions,
  useEstate_GetAllEstatesForEstateListDataGridLazyQuery,
  useFeatureSubscription_GetFeatureSubscriptionTypeQuery,
} from 'queries';
import userflow from 'userflow.js';

import Spinner from 'common/spinner';
import HomeLayout from 'layouts/home-layout';
import CreateEstateModal from 'pages/estates/components/create-estate-modal';
import { NodeEnvironment } from 'utils/enum';
import { canAddOrEditClientMatter } from 'utils/feature-checker';
import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';
import { defaultPaginationModel } from 'utils/pagination-model';
import useCurrentUser from 'utils/useCurrentUser';
import useHasPortalPermission from 'utils/useHasPortalPermission';

import ArchivedEstateList from './components/archived-estate-list';
import EstateImportList from './components/estate-import-list';
import EstateList from './components/estate-list';
import EstateTeamMembers from './components/estate-team-members';
import { PortalDocumentsForSignature } from './components/portal-documents-stats/portal-documents-stats';
import { PortalFavoriteTasks } from './components/portal-favorite-tasks/portal-favorite-tasks';
import { PortalTaskStats } from './components/portal-task-stats/portal-task-stats';
import SyncDecisionVaultModal from './components/sync-decision-vault-modal/sync-decision-vault-modal';

const useStyles = makeStyles((theme) => ({
  container: {
    color: '#1C2541',
    padding: theme.spacing(3),
  },
  pageHeader: {
    fontSize: '24px',
    fontWeight: '700',
  },
  headerIcon: {
    marginRight: '8px',
  },
  headerContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '32px',
  },
  addClientMatterBtn: {
    backgroundColor: '#0d0752',
    borderRadius: '8px',
    marginTop: 0,
  },
  bottomMargin: {
    marginBottom: '32px',
  },
  searchContainer: { maxWidth: '500px', marginBottom: '24px' },
  imgContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
    paddingRight: theme.spacing(6),
    paddingLeft: theme.spacing(6),
  },
  imgStyle: {
    height: theme.spacing(35),
    borderRadius: '14px',
    padding: '0',
    transition: 'transform 0.3s ease',
    '&:hover': {
      transform: 'scale(1.05)',
      cursor: 'pointer',
    },
  },
  homeContainerOverride: {
    marginTop: 0,
    padding: 0,
  },
}));

const DEFAULT_DATA_GRID_PAGE_SIZE = 25;

const HomePage = () => {
  const classes = useStyles();
  const { portalId } = useParams<Record<string, string>>();
  const [showArchivedEstates, setShowArchivedEstates] = useState(false);
  const [showCreateEstateModal, setShowCreateEstateModal] = useState(false);
  const [showDecisionVaultSyncModal, setShowDecisionVaultSyncModal] = useState(false);
  const [selectedEstate, setSelectedEstate] = useState<Estate | undefined>(undefined);
  const [selectedEstateImport, setSelectedEstateImport] = useState<EstateImport | null>(null);

  const currentUser = useCurrentUser();
  const hasPortalPermissionAdmin = useHasPortalPermission(PortalPermissions.Admin);
  const hasPortalPermissionUser = useHasPortalPermission(PortalPermissions.User);

  const { data: featureSubscriptionTypeData, loading: featureSubscriptionTypeDataLoading } =
    useFeatureSubscription_GetFeatureSubscriptionTypeQuery({
      variables: {
        portalId,
      },
      onError: (e) => {
        showAlert({
          message: 'Error getting feature subscription type: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });
  const [
    getAllEstatesForEstateListDataGridLazyQuery,
    getAllEstatesForEstateListDataGridLazyQueryResult,
  ] = useEstate_GetAllEstatesForEstateListDataGridLazyQuery({
    fetchPolicy: 'no-cache',
    nextFetchPolicy: 'no-cache',
    variables: {
      input: {
        portalId: portalId,
        paginationInput: {
          pageFilter: [
            {
              column: EstateListDataGridColumns.IsArchived,
              operator: 'equals',
              value: 'false',
            },
          ],
          page: defaultPaginationModel.page,
          pageSize: DEFAULT_DATA_GRID_PAGE_SIZE,
        },
      },
    },
    onError: (e) => {
      showAlert({
        message: 'Error finding estates by portal ID: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const [
    getAllArchivedEstatesForEstateListDataGridLazyQuery,
    getAllArchivedEstatesForEstateListDataGridLazyQueryResult,
  ] = useEstate_GetAllEstatesForEstateListDataGridLazyQuery({
    variables: {
      input: {
        portalId: portalId,
        paginationInput: {
          pageFilter: [
            {
              column: EstateListDataGridColumns.IsArchived,
              operator: 'equals',
              value: 'true',
            },
          ],
          page: defaultPaginationModel.page,
          pageSize: DEFAULT_DATA_GRID_PAGE_SIZE,
        },
      },
    },
    onError: (e) => {
      showAlert({
        message: 'Error finding estates by portal ID: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  useEffect(() => {
    getAllArchivedEstatesForEstateListDataGridLazyQuery();
  }, [getAllArchivedEstatesForEstateListDataGridLazyQuery]);

  useEffect(() => {
    if (
      featureSubscriptionTypeData?.data &&
      featureSubscriptionTypeData.data !== FeatureSubscriptionType.None &&
      currentUser?.id
    ) {
      // We want Userflow to be initiated on the home page for active subscribers
      if (
        process.env.REACT_APP_USERFLOW_TOKEN &&
        (process.env.REACT_APP_NODE_ENV === NodeEnvironment.UAT ||
          process.env.REACT_APP_NODE_ENV === NodeEnvironment.PROD)
      ) {
        // Only identify the user if they haven't been identified yet
        if (!userflow.isIdentified()) {
          userflow.identify(currentUser.id, {
            name: currentUser.firstName + ' ' + currentUser.lastName,
            email: currentUser.email,
          });
        }
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featureSubscriptionTypeData, currentUser]);

  if (featureSubscriptionTypeDataLoading || !featureSubscriptionTypeData?.data) {
    return <Spinner />;
  }

  return (
    <HomeLayout contentClassName={classes.homeContainerOverride}>
      <Fade in={true}>
        <div>
          {(hasPortalPermissionAdmin || hasPortalPermissionUser) && (
            <PortalTaskStats portalId={portalId} />
          )}
          <div className={classes.container}>
            <div>
              {(hasPortalPermissionAdmin || hasPortalPermissionUser) && (
                <PortalFavoriteTasks portalId={portalId} />
              )}

              {(hasPortalPermissionAdmin || hasPortalPermissionUser) && (
                <PortalDocumentsForSignature portalId={portalId} />
              )}

              <div className={classes.headerContainer}>
                <Typography className={classes.pageHeader}>My Projects</Typography>
                {(hasPortalPermissionAdmin || hasPortalPermissionUser) && (
                  <Button
                    variant="contained"
                    color="primary"
                    className={classes.addClientMatterBtn}
                    onClick={() => {
                      if (!canAddOrEditClientMatter(featureSubscriptionTypeData.data)) {
                        showToast({
                          message: 'Please upgrade your account to add a new client/matter.',
                          severity: NotificationSeverity.WARNING,
                        });
                        return;
                      }

                      setShowCreateEstateModal(true);
                    }}
                  >
                    <AddIcon className={classes.headerIcon} fontSize="small" />
                    Add Client Matter
                  </Button>
                )}
              </div>

              <EstateList
                featureSubscriptionType={featureSubscriptionTypeData.data}
                showArchivedEstates={showArchivedEstates}
                setShowArchivedEstates={setShowArchivedEstates}
                lazyQueryResult={getAllEstatesForEstateListDataGridLazyQueryResult}
                executeLazyQuery={getAllEstatesForEstateListDataGridLazyQuery}
                archivedLazyQueryResult={getAllArchivedEstatesForEstateListDataGridLazyQueryResult}
                setShowDecisionVaultSyncModal={setShowDecisionVaultSyncModal}
                setSelectedEstate={setSelectedEstate}
              />
              {showArchivedEstates && (
                <ArchivedEstateList
                  featureSubscriptionType={featureSubscriptionTypeData.data}
                  setShowArchivedEstates={setShowArchivedEstates}
                  lazyQueryResult={getAllEstatesForEstateListDataGridLazyQueryResult}
                  archivedLazyQueryResult={
                    getAllArchivedEstatesForEstateListDataGridLazyQueryResult
                  }
                  executeArchivedLazyQuery={getAllArchivedEstatesForEstateListDataGridLazyQuery}
                />
              )}

              {(hasPortalPermissionAdmin || hasPortalPermissionUser) && (
                <EstateImportList
                  featureSubscriptionType={featureSubscriptionTypeData.data}
                  setShowCreateEstateModal={setShowCreateEstateModal}
                  setSelectedEstateImport={setSelectedEstateImport}
                  setShowDecisionVaultSyncModal={setShowDecisionVaultSyncModal}
                />
              )}
            </div>
            {(hasPortalPermissionAdmin || hasPortalPermissionUser) && (
              <>
                <div>
                  <Typography className={clsx(classes.pageHeader, classes.bottomMargin)}>
                    My Team
                  </Typography>
                  <EstateTeamMembers
                    portalId={portalId}
                    featureSubscriptionType={featureSubscriptionTypeData.data}
                  />
                </div>
                <CreateEstateModal
                  showModal={showCreateEstateModal}
                  setShowModal={setShowCreateEstateModal}
                  estateImport={selectedEstateImport}
                  setSelectedEstateImport={setSelectedEstateImport}
                />
              </>
            )}
            {showDecisionVaultSyncModal && (
              <SyncDecisionVaultModal
                showModal={showDecisionVaultSyncModal}
                setShowModal={setShowDecisionVaultSyncModal}
                setSelectedEstate={setSelectedEstate}
                selectedEstate={selectedEstate}
              />
            )}
          </div>
        </div>
      </Fade>
    </HomeLayout>
  );
};

export default HomePage;
